"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Code, Gauge, Search, Network, Smartphone, FileCode2, CircleCheck as CheckCircle2, ArrowRight, ShieldCheck, Cpu } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
interface OptimizationItem {
  id: string;
  category: string;
  title: string;
  description: string;
  iconName: string;
  deliverables: string;
}
const optimizations: OptimizationItem[] = [{
  id: "titles-meta",
  category: "On-Page Foundations",
  title: "Title tags & meta descriptions",
  description: "I craft focused meta data for every priority URL, balancing target search queries with clear conversion hooks.",
  iconName: "Search",
  deliverables: "Exact character limits, search intent matching, CTR refinement"
}, {
  id: "heading-structure",
  category: "On-Page Foundations",
  title: "Headings & content hierarchy",
  description: "I restructure page templates into clean H1 to H4 hierarchies so search engines and visitors immediately grasp topical context.",
  iconName: "Code",
  deliverables: "Logical sectioning, semantic tags, zero heading skips"
}, {
  id: "content-depth",
  category: "On-Page Foundations",
  title: "Content quality & search intent",
  description: "I review your copy against real commercial queries, removing thin text and expanding answers that satisfy customer intent.",
  iconName: "CheckCircle2",
  deliverables: "Topical depth checks, query mapping, duplicate elimination"
}, {
  id: "internal-links",
  category: "On-Page Foundations",
  title: "Internal linking architecture",
  description: "I build clear pathways between your service pages and commercial hubs, transferring page authority to where it counts.",
  iconName: "Network",
  deliverables: "Contextual anchor text, silo structures, orphan URL fixes"
}, {
  id: "indexation-crawling",
  category: "Technical Architecture",
  title: "Indexation & crawl budget",
  description: "I configure your robots.txt, XML sitemaps, and canonical tags so Google prioritises your high-value pages.",
  iconName: "Cpu",
  deliverables: "Canonical audit, sitemap hygiene, crawl bloat reduction"
}, {
  id: "core-web-vitals",
  category: "Technical Architecture",
  title: "Site speed & Core Web Vitals",
  description: "I isolate script delays, render-blocking resources, and asset weights to get your LCP, INP, and CLS scores into green territory.",
  iconName: "Gauge",
  deliverables: "Image compression, cache headers, CSS/JS streamlining"
}, {
  id: "mobile-usability",
  category: "Technical Architecture",
  title: "Mobile responsive rendering",
  description: "I verify touch target sizing, viewport adaptability, and layout shifts across multiple devices for mobile-first indexing.",
  iconName: "Smartphone",
  deliverables: "Tap target spacing, dynamic font scaling, viewport safety"
}, {
  id: "structured-data",
  category: "Technical Architecture",
  title: "Structured data & schema markup",
  description: "I implement clean JSON-LD schema (LocalBusiness, Service, FAQ, Article) so your listings qualify for rich snippet visibility.",
  iconName: "FileCode2",
  deliverables: "Valid JSON-LD, local geo-data, schema validator tests"
}];
const renderIcon = (name: string) => {
  const iconProps = {
    className: "size-5 text-primary"
  };
  switch (name) {
    case "Search":
      return <Search {...iconProps} />;
    case "Code":
      return <Code {...iconProps} />;
    case "CheckCircle2":
      return <CheckCircle2 {...iconProps} />;
    case "Network":
      return <Network {...iconProps} />;
    case "Cpu":
      return <Cpu {...iconProps} />;
    case "Gauge":
      return <Gauge {...iconProps} />;
    case "Smartphone":
      return <Smartphone {...iconProps} />;
    case "FileCode2":
      return <FileCode2 {...iconProps} />;
    default:
      return <ShieldCheck {...iconProps} />;
  }
};
export default function OnPageTechnicalSeo() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="onpagetechnicalseo" data-nav="light" className="relative bg-background py-20 md:py-28 overflow-hidden text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header & Consultant Introduction */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start mb-16">
          <div className="lg:col-span-5 flex flex-col gap-4">
            <Badge variant="secondary" className="bg-secondary text-secondary-foreground w-fit rounded-md px-3 py-1 font-medium border border-border">
              Direct Hands-On Implementation
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
              On-page and technical SEO for Bedford businesses
            </h2>
          </div>

          <div className="lg:col-span-7 flex flex-col gap-5 pt-2 text-muted-foreground text-base sm:text-lg leading-relaxed">
            <p>
              When you work with me, you get direct technical execution rather
              than junior hand-offs or vague agency audits. I personally examine
              your website codebase, server response behaviour, and on-page
              architecture to eliminate technical debt holding back your organic
              growth.
            </p>
            <div className="flex flex-wrap items-center gap-6 pt-2 border-t border-border text-sm text-foreground">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-primary" />
                <span>Zero guesswork audits</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-primary" />
                <span>Direct code & CMS fixes</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-primary" />
                <span>UK search market focus</span>
              </div>
            </div>
          </div>
        </div>

        {/* Optimisation Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {optimizations.map((item, index) => <motion.div key={item.id} data-index={index} variants={itemVariants} whileHover={prefersReducedMotion ? {} : {
          y: -4
        }} className="h-full">
              <Card className="h-full border border-border bg-card shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md flex flex-col justify-between">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-4 mb-2">
                    <div className="flex items-center justify-center size-10 rounded-md bg-muted/60 border border-border">
                      {renderIcon(item.iconName)}
                    </div>
                    <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                      {item.category}
                    </span>
                  </div>
                  <CardTitle className="text-xl font-semibold text-card-foreground">
                    {item.title}
                  </CardTitle>
                  <CardDescription className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    {item.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="pt-3 border-t border-border/60">
                  <p className="text-xs text-muted-foreground font-sans">
                    <span className="font-semibold text-foreground">
                      Key focus:
                    </span>{" "}
                    {item.deliverables}
                  </p>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Bottom Call to Action */}
        <div className="mt-16 sm:mt-20 p-8 sm:p-10 rounded-2xl bg-muted/40 border border-border text-center flex flex-col items-center justify-center max-w-3xl mx-auto">
          <h3 className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground font-default mb-3">
            Find out what is holding your search rankings back
          </h3>
          <p className="text-muted-foreground text-sm sm:text-base mb-6 max-w-xl">
            I will run an initial review of your site architecture, speed, and
            on-page signals with zero obligation.
          </p>
          <Button asChild size="lg" className="rounded-md font-medium text-primary-foreground bg-primary hover:bg-primary/90 shadow-sm">
            <Link to="/SeoAuditsPage" className="inline-flex items-center gap-2">
              <span>Get a Free SEO Audit</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}